import {
  NameColumn,
  PhoneNumberColumn,
  SearchResultList,
  SearchResultRow,
} from "features/Header/PuzzelTelephone/components/CallControl/CatalogSearchResultsStyle";
import { useEffect, useRef } from "react";
import { useInfiniteScroll } from "shared/hooks/useInfiniteScroll";

export type ContactInfo = {
  readonly phoneNumber: string | undefined;
  readonly name: string;
};

type Props = {
  searchResults: ContactInfo[];
  selectedIndex: number;
  onPhoneNumberClick: (phoneNumber: string) => void;
  isCalling: boolean;
};

export const CatalogSearchResults = ({
  isCalling,
  onPhoneNumberClick,
  searchResults,
  selectedIndex,
}: Props) => {
  const searchResultsContainerRef = useRef<HTMLUListElement>(null);
  const { numberOfItemsToRender } = useInfiniteScroll({
    containerRef: searchResultsContainerRef,
    initialItemsToRender: 25,
    maxItemsToRender: searchResults.length,
  });

  useEffect(() => {
    const selectedElement = document.querySelector(
      `#catalog-search-result-${selectedIndex}`,
    );
    selectedElement?.scrollIntoView({ block: "nearest" });
  }, [selectedIndex]);

  // Numbers duplicate name+phoneNumber entries so keys stay unique without using array index.
  const keyOccurrences = new Map<string, number>();
  const resultsWithKeys = searchResults
    .slice(0, numberOfItemsToRender)
    .map((contact: ContactInfo) => {
      const baseKey = `${contact.name}-${contact.phoneNumber}`;
      const occurrence = keyOccurrences.get(baseKey) ?? 0;
      keyOccurrences.set(baseKey, occurrence + 1);
      return {
        contact,
        key: occurrence === 0 ? baseKey : `${baseKey}-${occurrence}`,
      };
    });

  return (
    <SearchResultList ref={searchResultsContainerRef}>
      {resultsWithKeys.map(({ contact, key }, index) => (
        <SearchResultRow
          key={key}
          id={`catalog-search-result-${index}`}
          $shouldHighlight={index === selectedIndex}
          $disabled={!contact.phoneNumber || isCalling}
          onClick={(event) => {
            event.stopPropagation();
            if (contact.phoneNumber) {
              onPhoneNumberClick(contact.phoneNumber);
            }
          }}
        >
          <NameColumn>{contact.name}</NameColumn>
          <PhoneNumberColumn>{contact.phoneNumber}</PhoneNumberColumn>
        </SearchResultRow>
      ))}
    </SearchResultList>
  );
};
